import Vue from "vue";
import Vuex from "vuex";
import User from "@/shared/lib/client-sdk/models/user";
import Account from "@/shared/lib/client-sdk/models/account";
import $ from "jquery";
import SystemPropertiesModule from "@/shared/store/modules/system-properties.js";
import nliraCustomBranding from "@/shared/store/modules/nlira-custom-branding.js";
import {
  hasContextTabs,
  holdsTier,
  isBelowAnchor,
  tiersFrom,
} from "@/advisor/lib/navigation";

Vue.use(Vuex);

// In-flight searches, one per tier: navigations fire faster than a search completes.
const tierRequests = {};

// The account the latest navigation asked for, or null where it asked for none. Every dispatch
// below claims it, and a load may only take effect while its claim still stands.
let lastRequestedAccountUuid = null;

// Navigation loads still in flight, so a record page's account cannot displace where the user has
// since asked to go.
let navigationLoadsInFlight = 0;

// A history navigation abandons whatever account was on its way. Heard here rather than from the
// router because the case that needs it is the one the router cannot report: pressing Back twice
// lands on the URL still on screen, which vue-router treats as a duplicate -- no guard, no hook,
// so nothing else would supersede the first navigation's claim.
//
// Ordering is safe: popstate listeners run synchronously, while a guard claims only after its
// first await.
window.addEventListener("popstate", () => {
  lastRequestedAccountUuid = null;
});

// Null where the search failed, so a failure is never cached as an answer.
function searchAccounts(searchHandler) {
  return new Promise((resolve) => {
    searchHandler.search((searchResult) => {
      if (!searchResult) {
        resolve(null);
        return;
      }

      resolve(
        searchResult.results.map((account) => ({
          id: account.id,
          displayName: account.displayName,
          accountTypeCode: account.accountTypeCode,
        }))
      );
    });
  });
}

function activeAccountsIn(searchHandler) {
  searchHandler.addPrivateFilter("Status", "equals", "active");

  return searchHandler;
}

function accountFor(uuid) {
  const account = new Account();
  account.set("id", uuid);

  return account;
}

// Who the user reaches at a tier, or null where no single endpoint answers it: advisors seen from
// a RIA have a firm in between and no route for the jump.
function tierSearchHandler(anchorAccount, tier) {
  const anchor = accountFor(anchorAccount.uuid);

  if (
    tier === "advisoryFirm" &&
    anchorAccount.accountTypeCode === "registeredInvestmentAdvisor"
  ) {
    return activeAccountsIn(anchor.advisoryFirmsSearchHandler());
  }

  if (tier === "advisor" && anchorAccount.accountTypeCode === "advisoryFirm") {
    return activeAccountsIn(anchor.subaccountSearchHandler());
  }

  return null;
}

// The accounts at one tier, however many requests that takes. Two of the three cases are a single
// endpoint; advisors seen from a RIA have none -- there is a firm in between -- so the firms are
// asked first and each one for its own advisors, at a request per firm, once per session.
function requestAccountsAtTier(anchorAccount, tier, commit) {
  const handler = tierSearchHandler(anchorAccount, tier);
  if (handler) return searchAccounts(handler);

  if (
    tier === "advisor" &&
    anchorAccount.accountTypeCode === "registeredInvestmentAdvisor"
  ) {
    return fetchTierAccounts(anchorAccount, "advisoryFirm", commit).then(
      (firms) => {
        // A firms list that could not be fetched is not an empty firms list: mapping over the
        // second answers "no advisors", and the caller would cache that as true.
        if (!firms) return null;

        return Promise.all(
          firms.map((firm) =>
            searchAccounts(
              activeAccountsIn(accountFor(firm.id).subaccountSearchHandler())
            )
          )
        ).then((perFirm) =>
          perFirm.some((advisors) => advisors === null)
            ? null
            : perFirm.reduce((all, advisors) => all.concat(advisors), [])
        );
      }
    );
  }

  return Promise.resolve([]);
}

// Concurrent callers of the same tier share one request; different tiers never wait on each other.
function fetchTierAccounts(anchorAccount, tier, commit) {
  if (tierRequests[tier]) return tierRequests[tier];

  tierRequests[tier] = requestAccountsAtTier(anchorAccount, tier, commit).then(
    (accounts) => {
      tierRequests[tier] = null;

      if (!accounts) return null;

      commit("setReachableAccountsForTier", { tier, accounts });
      return accounts;
    }
  );

  return tierRequests[tier];
}

function tiersBelowAnchor(anchorAccount) {
  return tiersFrom(
    anchorAccount.accountTypeCode,
    anchorAccount.maxAccountReach
  ).filter((tier) => tier !== anchorAccount.accountTypeCode);
}

/**
 * Auto Branding Plugin
 *
 * Automatically manages custom branding based on user authentication changes
 * Listens for user mutations and triggers appropriate branding actions
 */
const autoBrandingPlugin = (store) => {
  store.subscribe((mutation, state) => {
    // Auto-fetch branding when user is set or removed
    if (
      mutation.type === "setAuthenticatedUser" ||
      mutation.type === "removeAuthenticatedUser"
    ) {
      try {
        store.dispatch("nliraCustomBranding/fetchEffectiveCustomBrand");
      } finally {
        // Signal that branding is done loading
        store.commit("isLoadingAuthenticatedUser", false);
      }
    }
  });
};

// The toast waiting to be said, if one is. At module scope because it outlives the call that set
// it, and has to be cancellable by a navigation that overtakes that one.
let pendingToast = null;

// The per-tier selection kept across a page load. sessionStorage rather than local: one box per
// browser tab, gone when the tab is.
//
// Stamped with the user it belongs to, because the portal cannot tell a fresh sign-in from a
// reload of a session already open -- both arrive as setAuthenticatedUser, and the unified login
// happens on another origin, so there is no login of ours to clear it in. Without the stamp, the
// next person to sign in on a tab somebody walked away from inherits their firm.
const SELECTIONS_KEY = "advisor.selectedAccountsByTier";

// Storage throws on being touched at all in a private window or with site data blocked, which is
// never a reason to fail: without it the selection simply lasts as long as the page.
function readStoredSelections(userId) {
  try {
    const stored = JSON.parse(sessionStorage.getItem(SELECTIONS_KEY));

    return stored && stored.userId === userId ? stored.selections : {};
  } catch (error) {
    return {};
  }
}

function writeStoredSelections(userId, selections) {
  try {
    if (!userId) sessionStorage.removeItem(SELECTIONS_KEY);
    else {
      sessionStorage.setItem(
        SELECTIONS_KEY,
        JSON.stringify({ userId, selections })
      );
    }
  } catch (error) {
    // As above.
  }
}

// Kept out of the mutations so the storage lives in one place.
const selectionPersistencePlugin = (store) => {
  store.subscribe((mutation, state) => {
    const userId = state.authenticatedUser && state.authenticatedUser.id;

    // Every door into the app leads here: its own login, the return from the auth portal, and
    // every reload. Somebody else's selections are dropped rather than restored.
    if (mutation.type === "setAuthenticatedUser") {
      const selections = readStoredSelections(userId);

      store.commit("restoreSelectedAccounts", selections);
      writeStoredSelections(userId, selections);

      return;
    }

    if (mutation.type === "removeAuthenticatedUser") {
      writeStoredSelections(null);

      return;
    }

    if (
      mutation.type === "rememberSelectedAccountForTier" ||
      mutation.type === "forgetSelectedAccount"
    ) {
      writeStoredSelections(userId, state.selectedAccountUuidByTier);
    }
  });
};

export default new Vuex.Store({
  state: {
    authenticatedUser: null,
    masterAccount: null,
    sideNavCollapsed: false,
    isLoadingAuthenticatedUser: true,
    // Who the user reaches, keyed by tier. Absent means not asked yet; an empty array is a real
    // answer, so the two must stay distinguishable.
    reachableAccountsByTier: {},
    // The account last entered at each tier, so a tab returns there instead of asking again.
    // Mirrored into sessionStorage by the plugin above; nothing about it is sent to the server.
    selectedAccountUuidByTier: {},
    // The route that cannot be shown and why: "denied" where the user may not be there,
    // "unavailable" where the account framing it could not be loaded. Read above the router-view,
    // so the refused view never mounts, while the URL is left alone and a stale or pasted link can
    // still be read and corrected.
    //
    // A path rather than a flag, because a flag has to be cleared and neither moment is safe:
    // clearing when a navigation starts un-hides the refused view long enough for it to mount and
    // load, and clearing when one finishes wipes a refusal just decided.
    blockedRoute: null,
  },

  getters: {
    // The account the membership is anchored at, as opposed to the one being looked at. Derived
    // rather than stored: it already arrives inside authenticatedUser.
    anchorAccount(state) {
      const accountUser =
        state.authenticatedUser && state.authenticatedUser.accountUser;

      if (!accountUser || !accountUser.accountUuid) return null;

      return {
        uuid: accountUser.accountUuid,
        displayName: accountUser.accountDisplayName,
        accountTypeCode: accountUser.accountTypeCode,
        // Falls back to the anchor's own tier: without a reach there is no ladder at all, which
        // would refuse the user every tiered route, their own dashboard included.
        maxAccountReach:
          accountUser.maxAccountReach || accountUser.accountTypeCode,
      };
    },

    // Read by the bar, the drawer beside it and the three left-hand menus, so it is derived here
    // rather than five times over.
    hasContextTabs(state, getters) {
      return hasContextTabs(getters.anchorAccount);
    },

    // Whether the account on screen sits below the caller's anchor. The worksheets pass this to
    // the shared user controls: a tier-manage check belongs on a descendant target, never on the
    // caller's own account, where there is no tier below to check.
    currentAccountIsBelowAnchor(state, getters) {
      return isBelowAnchor(getters.anchorAccount, state.masterAccount);
    },

    // The accounts reachable at the tier being operated at. Derived here rather than in the
    // component that lists them, so whether to offer a choice and what it contains cannot drift.
    currentTierAccounts(state) {
      const currentAccount = state.masterAccount;
      if (!currentAccount) return [];

      return (
        state.reachableAccountsByTier[currentAccount.accountTypeCode] || []
      );
    },
  },

  mutations: {
    setAuthenticatedUser(state, user) {
      if (state.authenticatedUser != user) state.authenticatedUser = user;
    },
    removeAuthenticatedUser(state) {
      state.authenticatedUser = null;
      state.reachableAccountsByTier = {};
      state.selectedAccountUuidByTier = {};
      state.blockedRoute = null;
      state.masterAccount = null;
      // Along with the account it was about. It is said on a delay, so a sign-out inside that
      // window would otherwise put "you are now acting as" over the login screen.
      clearTimeout(pendingToast);
    },
    isLoadingAuthenticatedUser(state, loading) {
      state.isLoadingAuthenticatedUser = loading;
    },
    setMasterAccount(state, masterAccount) {
      state.masterAccount = masterAccount;
    },
    setMasterAccountSelectedPlanId(state, id) {
      if (state.masterAccount) state.masterAccount.selectedPlanId = id || null;
    },
    setSideNavCollapsed(state, sideNavCollapsed) {
      state.sideNavCollapsed = sideNavCollapsed;
    },
    setReachableAccountsForTier(state, { tier, accounts }) {
      Vue.set(state.reachableAccountsByTier, tier, accounts);
    },
    setBlockedRoute(state, blockedRoute) {
      state.blockedRoute = blockedRoute;
    },
    // Seeded when the user is known, which is also when a different user's selections are
    // discarded. Nothing reads this before then.
    restoreSelectedAccounts(state, selections) {
      state.selectedAccountUuidByTier = selections || {};
    },
    rememberSelectedAccountForTier(state, { tier, accountUuid }) {
      if (!tier || !accountUuid) return;

      Vue.set(state.selectedAccountUuidByTier, tier, accountUuid);
    },
    // Dropped once an account turns out to be refused, so its tab stops leading back to it. Keyed
    // by account, since the tier it belonged to is exactly what could not be loaded.
    forgetSelectedAccount(state, accountUuid) {
      Object.keys(state.selectedAccountUuidByTier)
        .filter((tier) => state.selectedAccountUuidByTier[tier] === accountUuid)
        .forEach((tier) => {
          Vue.delete(state.selectedAccountUuidByTier, tier);
          // And what was known about the tier, which counted this account among its own. Absent
          // means unasked, so the next ask counts what is actually left.
          Vue.delete(state.reachableAccountsByTier, tier);
        });
    },
  },
  actions: {
    refreshAuthenticatedUser({ commit, dispatch }, callback) {
      commit("isLoadingAuthenticatedUser", true);

      return new Promise((resolve) => {
        User.fetchAuthenticatedUser(function (user) {
          if (!user) commit("removeAuthenticatedUser");
          else commit("setAuthenticatedUser", user);

          if ($.isFunction(callback)) callback(user);

          resolve(user); // ✅ now dispatch(...) is awaitable
        });
      });
    },

    // Loads the account the route puts us in, before the page that renders against it appears.
    //
    // Before the navigation rather than after, because the page and the left navigation are built
    // from the loaded account: fetching afterwards renders the previous account's dashboard for as
    // long as the request takes. The path is what a refusal is recorded against.
    ensureCurrentAccount(
      { state, commit, dispatch, getters },
      { accountUuid, path, fromRecord = false }
    ) {
      // A record page names the account its record belongs to; it does not ask to be taken
      // anywhere. While a navigation is loading one, that is what the user asked for. Once the
      // navigation has finished, the record's page is destroyed and its own check covers that.
      if (fromRecord && navigationLoadsInFlight > 0) {
        return Promise.resolve(null);
      }

      // Claimed before either early return: both are real answers, and a navigation that gives
      // either still has to supersede whatever an earlier one left in flight. Claiming after them
      // is what let clicking a tab and clicking straight back leave the second navigation's URL
      // over the first navigation's account.
      lastRequestedAccountUuid = accountUuid || null;

      if (!accountUuid) return Promise.resolve(null);

      if (state.masterAccount && state.masterAccount.id === accountUuid) {
        return Promise.resolve(state.masterAccount);
      }

      // Taken before the account in context is replaced: whether there was one is what tells a
      // return to the anchor from a session starting there.
      const previousAccount = state.masterAccount;

      return new Promise((resolve) => {
        if (!fromRecord) navigationLoadsInFlight += 1;

        const account = new Account();
        account.set("id", accountUuid);

        account.refresh((response) => {
          if (!fromRecord) navigationLoadsInFlight -= 1;

          // Superseded: another navigation has since asked for somewhere else, so this answer is
          // about an account nobody is on. Dropped whole, refusal included.
          if (lastRequestedAccountUuid !== accountUuid) {
            resolve(null);
            return;
          }

          if (!response.successful) {
            // Blocked either way: letting the navigation through would leave the address bar
            // naming one account while everything around it described the one before, with
            // nothing on screen saying so.
            //
            // Out of the body, because `response.httpStatus` is jQuery's text status and never a
            // code. Absent where no envelope arrived, which is a network failure, not an answer.
            const httpStatusCode = response.data?.status?.httpStatusCode;

            // Forgotten only for a fact about the account -- not yours, or not there. Anything
            // else is passing, and must not cost the tier its selection.
            commit("setBlockedRoute", {
              path,
              reason: httpStatusCode === 403 ? "denied" : "unavailable",
            });

            if (httpStatusCode === 403 || httpStatusCode === 404) {
              commit("forgetSelectedAccount", accountUuid);
            }

            resolve(null);
            return;
          }

          // The other reason an account can be off limits: not one the user holds a role at. The
          // route could not answer it, and neither does the server -- it checks branch membership,
          // and an advisor under your firms is in your branch. Checked here because this is the
          // only place the context is ever set, so anything added later inherits it.
          if (!holdsTier(getters.anchorAccount, account.accountTypeCode)) {
            commit("setBlockedRoute", { path, reason: "denied" });
            resolve(null);
            return;
          }

          // Any refusal recorded for this route is void now its account has loaded. Held against a
          // path, it stops matching while the user is elsewhere but matches again the moment they
          // return, so a route that failed once would wear it for the rest of the session.
          commit("setBlockedRoute", null);

          commit("setMasterAccount", account);

          // Recorded once the account is loaded, since only then is its tier known.
          commit("rememberSelectedAccountForTier", {
            tier: account.accountTypeCode,
            accountUuid: account.id,
          });

          const isBelow = isBelowAnchor(getters.anchorAccount, account);

          // The tier's accounts, before the arrival is announced and before the navigation is let
          // through. They decide whether the drawer draws a selector, and so whether there is a
          // spark for the toast to wait out; asking here also puts the wait under the overlay the
          // guard already holds, rather than after the page has painted with the drawer empty.
          //
          // Settled either way: the guard awaits this before letting the navigation through, so a
          // list that failed must not leave the overlay up over a page that never arrives.
          dispatch("ensureReachableAccounts")
            .catch(() => {})
            .then(() => {
              // Asked again on the far side of the wait, as the load itself is: another
              // navigation may have claimed the context while the list was in flight, and this
              // arrival is then about an account nobody is on any more.
              if (lastRequestedAccountUuid !== accountUuid) {
                resolve(null);
                return;
              }

              // Said from here because this action is the one door the account in context moves
              // through: the action on an entity's detail page, a tab, the selector in the drawer
              // and a pasted URL all arrive by it.
              //
              // Below the anchor always, a cold landing deep in somebody else's account being as
              // much an arrival as a click. At the anchor only on the way back from somewhere,
              // since that is where a session starts and saying it on every login would be noise.
              if (isBelow || previousAccount) {
                // The snackbar is a singleton mounted once for the whole app, so it can be spoken
                // to directly and survives the navigation this arrival begins with. Replaced
                // rather than added to, so a second switch before the first has spoken cancels it.
                clearTimeout(pendingToast);

                // Last, so it waits out the motion it belongs to. The tab background always
                // travels, for 900ms; the spark runs for 1687ms after that, and only where the
                // control it is traced around exists -- below the anchor, with more than one
                // account to choose between.
                const marksArrival =
                  isBelow && getters.currentTierAccounts.length > 1;

                pendingToast = setTimeout(
                  function () {
                    window.$snackbar.show({
                      text:
                        "You are now acting as " + account.displayName + ".",
                      // Dressed in PcAdvisorApp, so that only this message rises from below the
                      // fold and every other snackbar keeps the entrance Vuetify gives it.
                      transition: "acting-as-toast",
                    });
                  },
                  marksArrival ? 2600 : 900
                );
              }

              resolve(account);
            });
        });
      });
    },

    // Safe to await on every navigation: tiers already in the store are skipped, and concurrent
    // callers of the same tier share one request.
    ensureReachableAccounts({ state, commit, getters }) {
      const anchorAccount = getters.anchorAccount;
      if (!anchorAccount) return Promise.resolve([]);

      const pending = tiersBelowAnchor(anchorAccount)
        .filter((tier) => !state.reachableAccountsByTier[tier])
        .map((tier) => fetchTierAccounts(anchorAccount, tier, commit));

      return Promise.all(pending);
    },
  },
  modules: {
    systemProperties: SystemPropertiesModule,
    nliraCustomBranding,
  },
  plugins: [autoBrandingPlugin, selectionPersistencePlugin],
});
